import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  API_BASE_URL,
  AuthorizationDeliveryMode,
  ClientCodeRequest,
  ProspectCodeRequest,
  ResumeSessionRequest,
  TokenResponse,
  UserLoginInfo,
  ValidateCodeRequest
} from '@webapi/MIF.Subscription.WebApi';
import { Observable, map, of } from 'rxjs';

type UserLoginInfoWithUserId = UserLoginInfo & { userId?: string };

@Injectable({
  providedIn: 'root'
})
export class MifSubscriptionAuthService {
  private readonly rawHttp: HttpClient;
  private readonly authRequestOptions = {
    headers: new HttpHeaders({
      Accept: 'application/json'
    })
  };

  constructor(
    httpBackend: HttpBackend,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {
    this.rawHttp = new HttpClient(httpBackend);
  }

  requestCodeForProspect(request: ProspectCodeRequest): Observable<UserLoginInfo> {
    return this.rawHttp
      .post<UserLoginInfoWithUserId>(`${this.apiBaseUrl}/Auth/request-code-prospect`, request, this.authRequestOptions)
      .pipe(map(response => this.mapUserLoginInfo(response)));
  }

  requestCodeForClient(request: ClientCodeRequest): Observable<UserLoginInfo> {
    return this.rawHttp
      .post<UserLoginInfoWithUserId>(`${this.apiBaseUrl}/Auth/request-code-client`, request, this.authRequestOptions)
      .pipe(map(response => this.mapUserLoginInfo(response)));
  }

  resumeSessionByUserId(
    userId: string,
    deliveryMode: AuthorizationDeliveryMode = AuthorizationDeliveryMode.Sms
  ): Observable<UserLoginInfo> {
    const trimmedUserId = userId.trim();

    if (!trimmedUserId) {
      return of(new UserLoginInfo());
    }

    const request = new ResumeSessionRequest({
      profileId: trimmedUserId,
      deliveryMode
    });

    return this.rawHttp
      .post<UserLoginInfoWithUserId>(`${this.apiBaseUrl}/Auth/resume-session`, request, this.authRequestOptions)
      .pipe(map(response => this.mapUserLoginInfo(response)));
  }

  validateCode(request: ValidateCodeRequest): Observable<TokenResponse> {
    return this.rawHttp
      .post<TokenResponse>(`${this.apiBaseUrl}/Auth/validate-code`, request, this.authRequestOptions)
      .pipe(map(response => TokenResponse.fromJS(response)));
  }

  private mapUserLoginInfo(response: UserLoginInfoWithUserId | null | undefined): UserLoginInfo {
    const authResponse = UserLoginInfo.fromJS(response) as UserLoginInfoWithUserId;

    authResponse.userId = response?.userId;

    return authResponse;
  }
}
